import {
  DEFAULT_ACM_ROUTES,
  DEFAULT_AMPLIFY_ROUTES,
  DEFAULT_API_GATEWAY_ROUTES,
  DEFAULT_APPCONFIG_ROUTES,
  DEFAULT_APPSYNC_ROUTES,
  DEFAULT_ATHENA_ROUTES,
  DEFAULT_BACKUP_ROUTES,
  DEFAULT_CLOUDFORMATION_ROUTES,
  DEFAULT_CLOUDFRONT_ROUTES,
  DEFAULT_CLOUDTRAIL_ROUTES,
  DEFAULT_CLOUDWATCH_ROUTES,
  DEFAULT_CODECOMMIT_ROUTES,
  DEFAULT_COGNITO_ROUTES,
  DEFAULT_DMS_ROUTES,
  DEFAULT_DOCDB_ROUTES,
  DEFAULT_DYNAMODB_ROUTES,
  DEFAULT_EC2_ROUTES,
  DEFAULT_ECR_ROUTES,
  DEFAULT_ECS_ROUTES,
  DEFAULT_EKS_ROUTES,
  DEFAULT_ELASTICACHE_ROUTES,
  DEFAULT_EVENTBRIDGE_ROUTES,
  DEFAULT_FIREHOSE_ROUTES,
  DEFAULT_GLUE_ROUTES,
  DEFAULT_IAM_ROUTES,
  DEFAULT_KAFKA_ROUTES,
  DEFAULT_KINESIS_ROUTES,
  DEFAULT_KMS_ROUTES,
  DEFAULT_LAMBDA_ROUTES,
  DEFAULT_MQ_ROUTES,
  DEFAULT_MWAA_ROUTES,
  DEFAULT_NEPTUNE_ROUTES,
  DEFAULT_OPENSEARCH_ROUTES,
  DEFAULT_RDS_ROUTES,
  DEFAULT_REDSHIFT_ROUTES,
  DEFAULT_ROUTE53_ROUTES,
  DEFAULT_S3_ROUTES,
  DEFAULT_SECRETSMANAGER_ROUTES,
  DEFAULT_SNS_ROUTES,
  DEFAULT_SQS_ROUTES,
  DEFAULT_STEPFUNCTIONS_ROUTES,
  DEFAULT_SYSTEMPARAMETERS_ROUTES,
  DEFAULT_TIMESTREAMDB_ROUTES,
} from '@localstack/constants';

export type ResourceRouteConfig = { route: string; paramKey: string; transformId?: (id: string) => string };

const r = (route: string | undefined, paramKey: string): ResourceRouteConfig => ({ route: route ?? '', paramKey });

// Maps AWS CloudFormation type segments to resource browser route configs.
// service = parts[1] of "AWS::Service::Type", resourceType = parts[2].
export const RESOURCE_ROUTE_MAPPING: Record<string, Record<string, ResourceRouteConfig>> = {
  Amplify: { App: r(DEFAULT_AMPLIFY_ROUTES.RESOURCES_AMPLIFY_APP, 'appId') },
  ApiGateway: { RestApi: r(DEFAULT_API_GATEWAY_ROUTES.RESOURCES_AGW1_API, 'restApiId') },
  ApiGatewayV2: { Api: r(DEFAULT_API_GATEWAY_ROUTES.RESOURCES_AGW2_API, 'apiId') },
  AppConfig: { Application: r(DEFAULT_APPCONFIG_ROUTES.RESOURCES_APPCONFIG_APPLICATION, 'id') },
  AppSync: { GraphQLApi: r(DEFAULT_APPSYNC_ROUTES.RESOURCES_APPSYNC_API, 'apiId') },
  Athena: { Database: r(DEFAULT_ATHENA_ROUTES.RESOURCES_ATHENA_DATABASE, 'databaseName') },
  Backup: {
    BackupPlan: r(DEFAULT_BACKUP_ROUTES.RESOURCES_BACKUP_PLAN, 'id'),
    BackupVault: r(DEFAULT_BACKUP_ROUTES.RESOURCES_BACKUP_VAULT, 'name'),
  },
  CertificateManager: { Certificate: r(DEFAULT_ACM_ROUTES.RESOURCES_ACM_CERTIFICATE, 'id') },
  CloudFormation: { Stack: r(DEFAULT_CLOUDFORMATION_ROUTES.RESOURCES_CLOUDFORMATION_STACK_RESOURCES, 'name') },
  CloudFront: { Distribution: r(DEFAULT_CLOUDFRONT_ROUTES.RESOURCES_CLOUDFRONT_DISTRIBUTION, 'id') },
  CloudTrail: { Trail: r(DEFAULT_CLOUDTRAIL_ROUTES.RESOURCES_CLOUDTRAIL_TRAIL, 'name') },
  CodeCommit: { Repository: r(DEFAULT_CODECOMMIT_ROUTES.RESOURCES_CODECOMMIT_REPOSITORY, 'id') },
  Cognito: { UserPool: r(DEFAULT_COGNITO_ROUTES.RESOURCES_COGNITO_USER_POOL, 'poolId') },
  DMS: {
    ReplicationInstance: r(DEFAULT_DMS_ROUTES.RESOURCES_DMS_REPLICATION_INSTANCE, 'id'),
    Endpoint: r(DEFAULT_DMS_ROUTES.RESOURCES_DMS_ENDPOINT, 'id'),
    ReplicationTask: r(DEFAULT_DMS_ROUTES.RESOURCES_DMS_REPLICATION_TASK, 'id'),
  },
  DocDB: {
    DBCluster: r(DEFAULT_DOCDB_ROUTES.RESOURCES_DOCDB_CLUSTER, 'id'),
    DBInstance: r(DEFAULT_DOCDB_ROUTES.RESOURCES_DOCDB_INSTANCE, 'id'),
  },
  DynamoDB: { Table: r(DEFAULT_DYNAMODB_ROUTES.RESOURCES_DYNAMODB_TABLE, 'tableName') },
  EC2: { Instance: r(DEFAULT_EC2_ROUTES.RESOURCES_EC2_INSTANCE, 'id') },
  ECR: { Repository: r(DEFAULT_ECR_ROUTES.RESOURCES_ECR_IMAGES, 'name') },
  ECS: {
    Cluster: r(DEFAULT_ECS_ROUTES.RESOURCES_ECS_CLUSTER, 'clusterName'),
    TaskDefinition: r(DEFAULT_ECS_ROUTES.RESOURCES_ECS_TASK_DEFINITION, 'family'),
  },
  EKS: { Cluster: r(DEFAULT_EKS_ROUTES.RESOURCES_EKS_CLUSTER, 'id') },
  ElastiCache: { CacheCluster: r(DEFAULT_ELASTICACHE_ROUTES.RESOURCES_ELASTICACHE_CLUSTER, 'clusterId') },
  Events: { EventBus: r(DEFAULT_EVENTBRIDGE_ROUTES.RESOURCES_EVENT_BRIDGE_BUS, 'busName') },
  Glue: {
    Database: r(DEFAULT_GLUE_ROUTES.RESOURCES_GLUE_DATABASE_TABLES, 'database'),
    Job: r(DEFAULT_GLUE_ROUTES.RESOURCES_GLUE_JOB, 'job'),
    Crawler: r(DEFAULT_GLUE_ROUTES.RESOURCES_GLUE_CRAWLER, 'crawler'),
    Connection: r(DEFAULT_GLUE_ROUTES.RESOURCES_GLUE_CONNECTION, 'connection'),
    Registry: r(DEFAULT_GLUE_ROUTES.RESOURCES_GLUE_REGISTRY, 'registry'),
  },
  IAM: {
    Group: r(DEFAULT_IAM_ROUTES.RESOURCES_IAM_GROUP, 'groupName'),
    Policy: r(DEFAULT_IAM_ROUTES.RESOURCES_IAM_POLICY, 'policyArn'),
    Role: r(DEFAULT_IAM_ROUTES.RESOURCES_IAM_ROLE, 'roleName'),
    User: r(DEFAULT_IAM_ROUTES.RESOURCES_IAM_USER, 'userName'),
  },
  Kinesis: { Stream: r(DEFAULT_KINESIS_ROUTES.RESOURCES_KINESIS_STREAM, 'streamName') },
  KinesisFirehose: { DeliveryStream: r(DEFAULT_FIREHOSE_ROUTES.RESOURCES_FIREHOSE_STREAM, 'deliveryStreamName') },
  KMS: { Key: r(DEFAULT_KMS_ROUTES.RESOURCES_KMS_KEY, 'keyId') },
  Lambda: { Function: r(DEFAULT_LAMBDA_ROUTES.RESOURCES_LAMBDA_FUNCTION, 'name') },
  Logs: { LogGroup: r(DEFAULT_CLOUDWATCH_ROUTES.RESOURCES_CLOUDWATCH_GROUP_EVENTS, 'logGroupName') },
  MSK: { Cluster: r(DEFAULT_KAFKA_ROUTES.RESOURCES_KAFKA_CLUSTER, 'clusterArn') },
  MWAA: { Environment: r(DEFAULT_MWAA_ROUTES.RESOURCES_MWAA_ENVIRONMENT, 'id') },
  AmazonMQ: { Broker: r(DEFAULT_MQ_ROUTES.RESOURCES_MQ_BROKER, 'id') },
  Neptune: {
    DBCluster: r(DEFAULT_NEPTUNE_ROUTES.RESOURCES_NEPTUNE_CLUSTER, 'clusterId'),
    DBInstance: r(DEFAULT_NEPTUNE_ROUTES.RESOURCES_NEPTUNE_INSTANCE, 'instanceId'),
  },
  OpenSearchService: { Domain: r(DEFAULT_OPENSEARCH_ROUTES.RESOURCES_OPENSEARCH_DOMAIN, 'domainName') },
  RDS: {
    DBCluster: r(DEFAULT_RDS_ROUTES.RESOURCES_RDS_CLUSTER, 'clusterId'),
    DBInstance: r(DEFAULT_RDS_ROUTES.RESOURCES_RDS_INSTANCE, 'instanceId'),
  },
  Redshift: { Cluster: r(DEFAULT_REDSHIFT_ROUTES.RESOURCES_REDSHIFT_CLUSTER, 'id') },
  Route53: { HostedZone: r(DEFAULT_ROUTE53_ROUTES.RESOURCES_ROUTE53_HOSTED_ZONE, 'hostedZone') },
  S3: { Bucket: r(DEFAULT_S3_ROUTES.RESOURCES_S3_BUCKET, 'bucket') },
  SecretsManager: { Secret: r(DEFAULT_SECRETSMANAGER_ROUTES.RESOURCES_SECRETS_MANAGER_SECRET, 'secretId') },
  SNS: { Topic: r(DEFAULT_SNS_ROUTES.RESOURCES_SNS_TOPIC, 'topicArn') },
  SQS: {
    Queue: { ...r(DEFAULT_SQS_ROUTES.RESOURCES_SQS_QUEUE, 'name'), transformId: (id) => id.split('/').at(-1) ?? id },
  },
  SSM: { Parameter: r(DEFAULT_SYSTEMPARAMETERS_ROUTES.RESOURCES_SYSTEM_PARAMETER, 'name') },
  StepFunctions: {
    StateMachine: r(DEFAULT_STEPFUNCTIONS_ROUTES.RESOURCES_STEPFUNCTIONS_STATE_MACHINE, 'stateMachineArn'),
  },
  Timestream: { Database: r(DEFAULT_TIMESTREAMDB_ROUTES.RESOURCES_TIMESTREAM_DATABASE, 'database') },
};

export const getResourceRouteConfig = (service: string, resourceType: string): ResourceRouteConfig | undefined =>
  RESOURCE_ROUTE_MAPPING[service]?.[resourceType];
